/**
 * @file label-sheets.ts
 * @description Расчёт листов самоклейки для диалога печати этикеток
 *   (phase19.22, inventory.md W17-5a/5b; ui-design §1, §2).
 *
 * Зачем файл существует: сводка «Будет 2 листа: стандарт ×24 (1 лист), мини
 * ×12 (1 лист)» и схема «Начать с N-й наклейки» обязаны считать листы так же,
 * как сервер (`label-print-plan.ts`): N−1 пустых клеток только на первом
 * листе шаблона, каждый шаблон — свои листы. Разойдутся — кладовщик положит в
 * принтер не то число листов. Чистые функции без React — проверяются тестом.
 */

import { pluralRu } from "./plural";
import {
  LABEL_TEMPLATE_META,
  LABEL_TEMPLATE_ORDER,
  type LabelStartAt,
  type LabelTemplate,
} from "./api-labels";

/** Потолки сервера (DTO печати): строка, всего, строк в запросе. */
export const LABEL_MAX_QTY_PER_LINE = 2000;
export const LABEL_MAX_TOTAL = 5000;
export const LABEL_MAX_LINES = 500;

/** Порог «проверьте запас самоклейки» (ui §2). */
export const LABEL_MANY_THRESHOLD = 500;

/** Сколько листов займут `labels` наклеек, если начать с `startAt`-й. */
export function sheetsFor(labels: number, startAt: number, perSheet: number): number {
  if (labels <= 0) return 0;
  const skip = clampStartAt(startAt, perSheet) - 1;
  return Math.ceil((labels + skip) / perSheet);
}

/** «Начать с N» в допустимых границах 1..на листе. */
export function clampStartAt(value: number, perSheet: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(Math.max(1, Math.trunc(value)), perSheet);
}

/** Строка для сводки: шаблон и количество этикеток. */
export interface LabelSheetLine {
  template: LabelTemplate;
  quantity: number;
}

/** Итог по шаблону. */
export interface LabelSheetGroup {
  template: LabelTemplate;
  labels: number;
  sheets: number;
}

/** Сводка по листам всей печати. */
export interface LabelSheetSummary {
  groups: LabelSheetGroup[];
  totalLabels: number;
  totalSheets: number;
}

/** Свести отмеченные строки в листы по шаблонам (порядок — как в PDF). */
export function summarizeSheets(lines: LabelSheetLine[], startAt: LabelStartAt): LabelSheetSummary {
  const byTemplate = new Map<LabelTemplate, number>();
  for (const line of lines) {
    if (line.quantity <= 0) continue;
    byTemplate.set(line.template, (byTemplate.get(line.template) ?? 0) + line.quantity);
  }
  const groups = LABEL_TEMPLATE_ORDER.filter((t) => byTemplate.has(t)).map((template) => {
    const labels = byTemplate.get(template) ?? 0;
    const perSheet = LABEL_TEMPLATE_META[template].perSheet;
    return { template, labels, sheets: sheetsFor(labels, startAt[template] ?? 1, perSheet) };
  });
  return {
    groups,
    totalLabels: groups.reduce((sum, g) => sum + g.labels, 0),
    totalSheets: groups.reduce((sum, g) => sum + g.sheets, 0),
  };
}

/** «Будет 2 листа: стандарт ×24 (1 лист), мини ×12 (1 лист)». */
export function formatSheetsSummary(summary: LabelSheetSummary): string {
  if (summary.totalSheets === 0) return "Отметьте позиции и укажите количество";
  const parts = summary.groups.map(
    (g) =>
      `${LABEL_TEMPLATE_META[g.template].short} ×${g.labels} (${g.sheets} ${pluralRu(g.sheets, ["лист", "листа", "листов"])})`,
  );
  const sheets = pluralRu(summary.totalSheets, ["лист", "листа", "листов"]);
  return `Будет ${summary.totalSheets} ${sheets}: ${parts.join(", ")}`;
}

/** Строка, для которой считаем количество: принято / остаток + признак развеса. */
export interface LabelQtySource {
  quantity: string | number;
  /**
   * Развес (фасовка = 1 г/кг/мл/л, `SkuPrintInfo.isMeasured` сервера): этикетка
   * на тару. `undefined` — старый сервер: тогда развесом считаем дробное число.
   */
  isMeasured?: boolean;
}

/**
 * Количество этикеток по умолчанию (решение пользователя 2026-09-25 п.6):
 * штучный товар — по количеству (принято / остаток), развес — одна этикетка
 * на тару (W21). Признак развеса — с сервера, а не по виду числа: «5 кг» —
 * целое, но это пять килограммов в одной таре. Не больше потолка строки.
 */
export function defaultLabelQty({ quantity, isMeasured }: LabelQtySource): number {
  const value = Number(quantity);
  if (!Number.isFinite(value) || value <= 0) return 1;
  if (isMeasured === true) return 1;
  if (isMeasured === undefined && !Number.isInteger(value)) return 1;
  return Math.min(Math.max(1, Math.round(value)), LABEL_MAX_QTY_PER_LINE);
}

/** Номера клеток схемы листа, которые уже использованы («начать с N»). */
export function usedCells(startAt: number, perSheet: number): number {
  return clampStartAt(startAt, perSheet) - 1;
}
